<template>
  <div ref="dropdown" :class="classes" class="app-select-dropdown">
    <div
      ref="scrollArea"
      :style="convertedMaxHeight"
      class="app-select-dropdown__scroll-area"
    >
      <ul ref="options" class="app-select-dropdown__options">
        <AppLoading
          v-if="loading"
          :size="20"
          class="app-select-dropdown__loading"
          position="center"
        />
        <slot v-else-if="!options.length" name="empty-container">
          <div class="app-select-dropdown__empty">
            <slot v-if="!hasSearchText" name="remoteSearchText"
              >Введите значение...</slot
            >
            <slot v-else name="empty">Данные отсутствуют</slot>
          </div>
        </slot>
        <template v-else>
          <AppSelectOption
            v-for="(option, index) in options"
            :key="index"
            :selected="checkSelection(option)"
            :hovered="index === typeAheadPointer"
            @mousedown.native.prevent.stop="$emit('select', option)"
            @mouseover.native="typeAheadPointer = index"
          >
            <slot :option="option" :index="index" name="option">
              {{ option[labelKey] }}
            </slot>
          </AppSelectOption>
        </template>
      </ul>
    </div>
  </div>
</template>

<script>
import { convertToUnit } from "@utils/helpers";
import AppSelectOption from "./AppSelectOption";

export default {
  name: "AppSelectDropdown",

  components: {
    AppSelectOption
  },

  props: {
    value: {},
    multiple: Boolean,
    opened: Boolean,
    labelKey: {
      type: String,
      default: "title"
    },
    valueKey: {
      type: String,
      default: "value"
    },
    checkSelection: {
      type: Function,
      required: true
    },
    options: {
      type: Array,
      default: () => []
    },
    maxHeight: {
      type: [String, Number],
      default: 190
    },
    loading: Boolean,
    hasSearchText: Boolean
  },

  data: () => ({
    typeAheadPointer: -1,
    stackMinZIndex: 20
  }),

  computed: {
    multipleAndValue() {
      return this.multiple && this.value;
    },
    convertedMaxHeight() {
      return {
        "max-height": convertToUnit(this.maxHeight)
      };
    },
    classes() {
      return {};
    }
  },

  watch: {
    "options.length": "setPointer",
    opened(val) {
      val && this.setPointer();
    }
  },

  methods: {
    getScrollElement() {
      return this.$refs.scrollArea;
    },
    typeAheadUp() {
      if (this.typeAheadPointer > 0) {
        this.typeAheadPointer--;
        this.adjustScroll();
      }
    },
    typeAheadDown() {
      if (this.typeAheadPointer < this.options.length - 1) {
        this.typeAheadPointer++;
        this.adjustScroll();
      }
    },
    typeAheadSelect() {
      if (this.options[this.typeAheadPointer]) {
        this.$emit("select", this.options[this.typeAheadPointer]);
      }
    },
    pixelsToPointerTop() {
      let pixelsToPointerTop = 0;
      for (let i = 0; i < this.typeAheadPointer; i++) {
        pixelsToPointerTop += this.$refs.options.children[i].offsetHeight;
      }
      return pixelsToPointerTop;
    },
    pixelsToPointerBottom() {
      return this.pixelsToPointerTop() + this.pointerHeight();
    },
    pointerHeight() {
      const element = this.$refs.options.children[this.typeAheadPointer];
      return element ? element.offsetHeight : 0;
    },
    adjustScroll() {
      const pixelsToPointerTop = this.pixelsToPointerTop();
      const pixelsToPointerBottom = this.pixelsToPointerBottom();
      const scrollElement = this.getScrollElement();

      if (pixelsToPointerTop <= this.viewport().top) {
        scrollElement.scrollTop = pixelsToPointerTop;
      } else if (pixelsToPointerBottom >= this.viewport().bottom) {
        scrollElement.scrollTop = this.viewport().top + this.pointerHeight();
      }
    },
    viewport() {
      const scrollElement = this.getScrollElement();
      return {
        top: scrollElement.scrollTop,
        bottom: scrollElement.offsetHeight + scrollElement.scrollTop
      };
    },
    scrollToSelected() {
      const scrollElement = this.getScrollElement();
      const el = scrollElement.querySelector(".app-select-option.is-selected");

      if (!el) return;

      const optionHalfHeight = el.offsetHeight / 2;
      const scrollTop = el.offsetTop - (this.maxHeight / 2 - optionHalfHeight);

      scrollElement.scrollTop = scrollTop;
    },

    setPointer() {
      let val;
      const { multiple, value, options, valueKey } = this;
      if (!multiple && value) val = value;
      if (multiple && value.length) val = value[0];

      this.typeAheadPointer = options.findIndex(item => item[valueKey] === val);
    }
  }
};
</script>

<style lang="scss">
$p: ".app-select-dropdown";
.app-select-dropdown {
  position: relative;
  background-color: #0a1534;
  overflow: hidden;
  border-bottom-left-radius: 25px;
  border-bottom-right-radius: 25px;
  padding: 5px 20px 15px;

  &__empty {
    text-align: center;
    line-height: 28px;
    font-size: 15px;
  }

  &__options {
    overflow: hidden;
    position: relative;
    min-height: 28px;
  }

  &__scroll-area {
    max-height: 200px;
    overflow-x: hidden;
    overflow-y: auto;
    z-index: 1;
    position: relative;

    scrollbar-width: thin;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: darken(#f72257, 10%);
    }

    &::-webkit-scrollbar-thumb:hover {
      background: darken(#f72257, 0);
    }
  }
}
</style>
